import React, { useEffect, useState } from 'react';
import logo from '../Logo-Big.jpg';
import { AppMode } from '../types';
import { useKeycloak } from '@react-keycloak/web';
import AblaufBanner from '../portal/AblaufBanner';
import { AusweisInfo, getMe, getRechte, getViewAs, isDemoMode, isHotMode, listMitglieder, MitgliedEintrag, setDemoMode, setHotMode, setViewAs, ZuepStatusInfo } from '../portal/ApiClient';
import SelbstFreigabeCard from '../portal/SelbstFreigabeCard';
import { computeTileStates, TileState } from '../portal/tileGating';
import './ModeSelection.css';

type ModeSelectionProps = {
  onSelect: (mode: AppMode) => void;
};

const ADMIN_ROLE = 'zuep-admin';

/** Zeitraum-/Hinweis-Zeile einer Kachel (aus dem Gating). */
function TileInfo({ state }: { state: TileState }) {
  if (!state.zeitraum && !state.hinweis) return null;
  return (
    <div className="mode-card__gating">
      {state.zeitraum && <div className="mode-card__zeitraum">🕐 {state.zeitraum}</div>}
      {state.hinweis && <div className="mode-card__hinweis">{state.hinweis}</div>}
    </div>
  );
}

function ModeSelection({ onSelect }: ModeSelectionProps) {
  const { keycloak } = useKeycloak();
  const [exiting, setExiting] = useState(false);
  const [selectedMode, setSelectedMode] = useState<AppMode | null>(null);
  // Admin-Rechte kommen aus UNSEREM Gateway (ADMIN_USERS + admin_user-DB),
  // nicht aus Keycloak -- der Verein hat dort keinen Admin-Zugriff. Die
  // Token-Rolle bleibt als Fallback berücksichtigt (Server prüft ohnehin).
  const [isAdmin, setIsAdmin] = useState(
    (keycloak.tokenParsed?.realm_access?.roles || []).includes(ADMIN_ROLE),
  );
  useEffect(() => {
    let cancelled = false;
    getRechte(keycloak.token)
      .then((r) => {
        if (!cancelled && r.admin) setIsAdmin(true);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [keycloak.token]);

  // Admins können in die Mitglieder-Sicht wechseln (sehen dann exakt das
  // Gating wie ein normales Mitglied). Merken über die Session hinaus.
  const [memberView, setMemberView] = useState(
    () => localStorage.getItem('zuep-member-view') === '1',
  );
  const toggleMemberView = () => {
    setMemberView((v) => {
      localStorage.setItem('zuep-member-view', v ? '0' : '1');
      return !v;
    });
  };
  const effectiveAdmin = isAdmin && !memberView;

  // Demo-/Hot-Modus (Admin-Werkzeuge): Demo = nichts wird echt verändert;
  // Hot = sofort ausführen, auch wenn nicht fällig. Wirken über Header auf
  // ALLE API-Aufrufe (ApiClient.applyModeHeaders), server-seitig geprüft.
  // Ansicht als anderes Mitglied (nur lesend, Server erzwingt GET+Admin).
  const [viewAs] = useState(getViewAs());
  const [viewAsInput, setViewAsInput] = useState('');
  const [mitglieder, setMitglieder] = useState<MitgliedEintrag[]>([]);
  const applyViewAs = (nr: string) => {
    setViewAs(nr);
    window.location.reload(); // alle Daten frisch unter der neuen Sicht laden
  };
  // Mitglieder-Liste fürs Dropdown (nur Admins; einmal laden).
  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    listMitglieder(keycloak.token)
      .then((r) => {
        if (!cancelled) setMitglieder(r.mitglieder);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const [demoMode, setDemoModeState] = useState(isDemoMode());
  const [hotMode, setHotModeState] = useState(isHotMode());
  const toggleDemo = () => {
    setDemoMode(!demoMode);
    setDemoModeState(!demoMode);
  };
  const toggleHot = () => {
    setHotMode(!hotMode);
    setHotModeState(!hotMode);
  };

  // Signatur-Gültigkeit der ANLAGE-4B-Vorlage (nur Admin-Anzeige).
  useEffect(() => {
    if (!isAdmin) return;
  }, [isAdmin]);

  // EIN /api/me-Fetch für Banner + Kachel-Gating.
  const [zs, setZs] = useState<ZuepStatusInfo | null>(null);
  const [auslandsWohnsitz, setAuslandsWohnsitz] = useState(false);
  // Italien-Bezug (IT-Wohnsitz in den letzten 5 Jahren oder in Italien
  // geboren) -> Kachel "Strafregisterauszug (Italien)" anzeigen.
  const [itBezug, setItBezug] = useState(false);
  const [ausweisInfo, setAusweisInfo] = useState<AusweisInfo | null>(null);
  const [docGueltig, setDocGueltig] = useState<boolean | null>(null);
  const [docAblauf, setDocAblauf] = useState<Date | null>(null);
  const [meState, setMeState] = useState<'loading' | 'ok' | 'none' | 'error'>('loading');

  useEffect(() => {
    let cancelled = false;
    getMe(keycloak.token)
      .then((me) => {
        if (!cancelled) {
          setZs(me.zuep_status ?? null);
          setAusweisInfo(me.ausweis_info ?? null);
          // Ist das hinterlegte Ausweisdokument bei der nächsten Verlängerung
          // (~ZÜP-Frist) noch gültig? (Schätzung: Ausstellung + 10 Jahre.)
          const idDocs = (me.dokinfo || []).filter((d) => (d.art === 1 || d.art === 2) && d.ausstellung);
          if (idDocs.length > 0) {
            const latest = idDocs.reduce((a, b) => (a.ausstellung > b.ausstellung ? a : b));
            const ablauf = new Date(`${latest.ausstellung}T00:00:00`);
            ablauf.setFullYear(ablauf.getFullYear() + 10);
            setDocAblauf(ablauf);
            const frist = me.zuep_status?.frist ? new Date(`${me.zuep_status.frist}T00:00:00`) : null;
            setDocGueltig(frist ? ablauf.getTime() > frist.getTime() + 28 * 86_400_000 : null);
          } else {
            setDocAblauf(null);
            setDocGueltig(null);
          }
          // Behörden-Regel (Bartek, 31.08.2026): Ein FRISCHER Strafregister-
          // auszug ist nur bei AKTUELLEM Auslands-Wohnsitz nötig; für frühere
          // Auslands-Wohnsitze genügt die einmalige Vorlage. Der Warnhinweis
          // gilt daher nur für aktuelle Auslands-Wohnsitze.
          const fuenfJahre = new Date();
          fuenfJahre.setFullYear(fuenfJahre.getFullYear() - 5);
          setAuslandsWohnsitz(
            (me.adressen || []).some((a) => !a.bis && a.land && a.land !== 'AT'),
          );
          setItBezug(
            (me.adressen || []).some(
              (a) => a.land === 'IT' && (!a.bis || new Date(`${a.bis}T00:00:00`) >= fuenfJahre),
            ) || me.person?.geb_ort_land === 'IT',
          );
          setMeState('ok');
        }
      })
      .catch((err: { status?: number }) => {
        if (!cancelled) setMeState(err?.status === 404 ? 'none' : 'error');
      });
    return () => {
      cancelled = true;
    };
  }, [keycloak.token]);

  // Solange /api/me lädt (oder bei Fehler), bleiben die Kacheln wie bisher
  // alle aktiv -- Gating greift erst mit belastbaren Daten.
  const gated = meState === 'ok' || meState === 'none';
  const tiles = gated
    ? computeTileStates(zs, {
        isAdmin: effectiveAdmin,
        unlinked: meState === 'none',
        auslandsWohnsitz,
        ausweisGueltigBis: ausweisInfo?.gueltig_bis,
        ausweisStand: ausweisInfo?.stand,
      })
    : null;

  const handleSelect = (mode: AppMode) => {
    setSelectedMode(mode);
    setExiting(true);
    setTimeout(() => onSelect(mode), 400);
  };

  /** Klick-Handler + Klassen für eine gegatete Kachel. */
  const tileProps = (mode: AppMode, state: TileState | undefined, extra = '') => {
    const disabled = state !== undefined && !state.enabled;
    return {
      className: `mode-card${extra} ${selectedMode === mode ? 'mode-card--selected' : ''} ${disabled ? 'mode-card--disabled' : ''}`,
      onClick: () => {
        if (!disabled) handleSelect(mode);
      },
    };
  };

  return (
    <div className={`mode-selection ${exiting ? 'exiting' : ''}`}>
      <div className="mode-bg-shape mode-bg-shape--1" />
      <div className="mode-bg-shape mode-bg-shape--2" />

      <div className="mode-header">
        <img src={logo} alt="Flugsportzentrum Tirol" className="mode-logo" />
        <h1 className="mode-title">ZÜP & Flughafenausweis</h1>
        <p className="mode-subtitle">Was möchten Sie tun?</p>
        <div className="mode-user-info">
          <span>{keycloak.tokenParsed?.name || keycloak.tokenParsed?.preferred_username || ''}</span>
          {isAdmin && (
            <button
              className="mode-logout-btn"
              title="Zwischen Admin-Sicht (alle Kacheln aktiv) und der echten Mitglieder-Sicht wechseln"
              onClick={toggleMemberView}
            >
              {memberView ? '👤 Mitglieder-Sicht (klicken für Admin)' : '🛠 Admin-Sicht (klicken für Mitglied)'}
            </button>
          )}
          {isAdmin && (
            <button
              className="mode-logout-btn"
              title="Demo-Modus: alle Schritte durchspielbar, aber es wird NICHTS gespeichert und NICHTS an die Behörde übermittelt"
              onClick={toggleDemo}
            >
              {demoMode ? '🧪 Demo AN' : '🧪 Demo aus'}
            </button>
          )}
          {isAdmin && !memberView && (
            <button
              className="mode-logout-btn"
              title="Hot-Modus: Aktionen sofort ausführen, auch wenn sie laut Zeitfenster nicht fällig sind (Doppel-Freigabe-Schutz bleibt aktiv)"
              onClick={toggleHot}
            >
              {hotMode ? '🔥 Hot AN' : '🔥 Hot aus'}
            </button>
          )}
          <button className="mode-logout-btn" onClick={() => keycloak.logout()}>Abmelden</button>
        </div>
        {isAdmin && !viewAs && (
          <div style={{ marginTop: 6, display: 'flex', gap: 6, justifyContent: 'center', alignItems: 'center' }}>
            <select
              value={viewAsInput}
              onChange={(e) => setViewAsInput(e.target.value)}
              style={{ maxWidth: 260, padding: '3px 8px', borderRadius: 6, border: '1px solid #ccc', fontSize: 13 }}
            >
              <option value=''>Mitglied wählen…</option>
              {mitglieder.map((m) => (
                <option key={m.mgd_nummer} value={m.mgd_nummer}>
                  {m.name ? `${m.name} (${m.mgd_nummer})` : m.mgd_nummer}
                </option>
              ))}
            </select>
            <button
              className="mode-logout-btn"
              disabled={!viewAsInput}
              title="Zeigt das Portal (nur lesend) mit den Daten dieses Mitglieds — z.B. um dessen Status/Kacheln zu sehen"
              onClick={() => applyViewAs(viewAsInput)}
            >
              👁 Als Mitglied ansehen
            </button>
          </div>
        )}
        {viewAs && (
          <div className="mode-demo-banner" style={{ borderColor: '#1565c0', color: '#0d47a1', background: '#e3f2fd' }}>
            👁 Du siehst gerade die Daten von Mitglied <strong>{viewAs}</strong> (nur lesend).{' '}
            <button
              className="mode-logout-btn"
              style={{ marginLeft: 8 }}
              onClick={() => applyViewAs('')}
            >
              Eigene Ansicht
            </button>
          </div>
        )}
        {demoMode && (
          <div className="mode-demo-banner">
            🧪 Demo-Modus aktiv — alle Schritte sind durchspielbar, es werden KEINE echten Daten
            verändert und nichts an die Behörde übermittelt.
          </div>
        )}
        {hotMode && !demoMode && (
          <div className="mode-demo-banner" style={{ borderColor: '#e65100', color: '#bf360c', background: '#fff3e0' }}>
            🔥 Hot-Modus aktiv — Aktionen werden sofort ausgeführt, auch außerhalb der Zeitfenster.
          </div>
        )}
        <AblaufBanner zuepStatus={zs} state={meState} ausweisInfo={ausweisInfo} />
        {meState === 'ok' && (
          <div style={{ marginTop: 6, marginBottom: 2 }}>
            <a
              href='https://ical.flugsportzentrum.at/'
              target='_blank'
              rel='noreferrer'
              style={{ fontSize: 14 }}
              title='Persönlichen FZT-Kalender abonnieren: Flugzeug-Reservierungen, ZÜP-, Ausweis- und Strafregister-Fälligkeiten — aktualisiert sich automatisch in deiner Kalender-App'
            >
              📅 FZT-Kalender abonnieren — Reservierungen &amp; alle Fälligkeiten
            </a>
          </div>
        )}
        {meState === 'ok' && zs?.code === 'zuverlaessig' && (
          <>
            {docGueltig === true && docAblauf && (
              <div className="mode-vorbereitung mode-vorbereitung--gruen">
                ✅ Dein hinterlegter Ausweis ist bei der nächsten Verlängerung noch gültig
                (bis ~{docAblauf.toLocaleDateString('de-AT')}) — <strong>kein neuer Scan nötig</strong>.
              </div>
            )}
            {docGueltig === false && (
              <div className="mode-vorbereitung mode-vorbereitung--amber">
                📋 Dein hinterlegter Ausweis läuft vor der nächsten Verlängerung ab — du brauchst
                einen <strong>aktuellen Scan deines neuen Ausweisdokuments</strong>. Du kannst ihn
                jederzeit über die Kachel <strong>Dokument nachreichen</strong> hochladen — oder
                später direkt im Verlängerungs-Formular.
              </div>
            )}
            {auslandsWohnsitz && (
              <div className="mode-vorbereitung mode-vorbereitung--amber">
                📋 <strong>Wichtig (Auslands-Wohnsitz):</strong> Für deine Verlängerung brauchst du eine{' '}
                <strong>Strafregisterbescheinigung</strong> aus deinem Wohnsitzland (bei Einreichung max.
                6 Monate alt). Kümmere dich <strong>rechtzeitig</strong> darum
                {zs?.frist && (() => {
                  const f = new Date(`${zs.frist}T00:00:00`);
                  const ab = new Date(f.getTime() - 60 * 86_400_000);
                  return <> — am besten ab dem <strong>{ab.toLocaleDateString('de-AT')}</strong> (ca. 2 Monate vor Ablauf)</>;
                })()}
                . Upload direkt im Verlängerungs-Formular; der abonnierte 📅 FZT-Kalender erinnert dich.
              </div>
            )}
            <SelbstFreigabeCard zuepStatus={zs} />
          </>
        )}
      </div>

      <div className="mode-cards">

        <div {...tileProps('zuep-verlaengerung', tiles?.verlaengerung)}>
          <div className="mode-card__accent mode-card__accent--renewal" />
          <div className="mode-card__icon mode-card__icon--renewal">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.3" />
            </svg>
          </div>
          <h2 className="mode-card__title">ZÜP-Verlängerung</h2>
          <p className="mode-card__desc">Jährliche Erneuerung der Zuverlässigkeitsüberprüfung</p>
          <ul className="mode-card__features">
            <li>Vorjahresdaten laden & aktualisieren</li>
            <li>Personendaten, Wohnsitze, Beschäftigungen</li>
            <li>ZIP-Datei für den Import generieren</li>
          </ul>
          {tiles && <TileInfo state={tiles.verlaengerung} />}
          <div className="mode-card__cta">
            <span className="mode-card__btn">Weiter</span>
            <svg className="mode-card__arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </div>
        </div>


        <div {...tileProps('neuantrag', tiles?.komplett, ' mode-card--primary')}>
          <div className="mode-card__badge">Komplett</div>
          <div className="mode-card__icon mode-card__icon--new">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 8v8M8 12h8" />
            </svg>
          </div>
          <h2 className="mode-card__title">Komplett-Antrag</h2>
          <p className="mode-card__desc">ZÜP + Flughafenausweis — bei Neubeantragung oder wenn beides gleichzeitig abläuft</p>
          <ul className="mode-card__features">
            <li>Alle ZÜP-Daten erfassen</li>
            <li>ANTRAG & ANLAGE 4B PDFs vorausgefüllt</li>
            <li>Rechnungsempfänger für Flughafengebühr</li>
          </ul>
          {tiles && <TileInfo state={tiles.komplett} />}
          <div className="mode-card__cta">
            <span className="mode-card__btn mode-card__btn--primary">Weiter</span>
            <svg className="mode-card__arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </div>
        </div>


        <div {...tileProps('ausweis-verlaengerung', tiles?.ausweis)}>
          <div className="mode-card__accent mode-card__accent--badge" />
          <div className="mode-card__icon mode-card__icon--badge">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="5" width="20" height="14" rx="2" />
              <circle cx="8" cy="12" r="2" />
              <path d="M14 10h4M14 14h4" />
            </svg>
          </div>
          <h2 className="mode-card__title">Flughafenausweis-Verlängerung</h2>
          <p className="mode-card__desc">Nur Ausweis-Verlängerung — ZÜP ist noch gültig</p>
          <ul className="mode-card__features">
            <li>Persönliche Daten eingeben oder aus Vorjahr laden</li>
            <li>ANTRAG & ANLAGE 4B PDFs vorausgefüllt</li>
            <li>Keine erneute ZÜP-Dateneingabe nötig</li>
          </ul>
          {tiles && <TileInfo state={tiles.ausweis} />}
          <div className="mode-card__cta">
            <span className="mode-card__btn">Weiter</span>
            <svg className="mode-card__arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </div>
        </div>

        <div
          className="mode-card"
          onClick={() => handleSelect('bg-wechsel')}
        >
          <div className="mode-card__accent mode-card__accent--renewal" />
          <div className="mode-card__icon mode-card__icon--renewal">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 1l4 4-4 4" /><path d="M3 11V9a4 4 0 0 1 4-4h14" />
              <path d="M7 23l-4-4 4-4" /><path d="M21 13v2a4 4 0 0 1-4 4H3" />
            </svg>
          </div>
          <h2 className="mode-card__title">ZÜP zu uns übertragen</h2>
          <p className="mode-card__desc">Deine ZÜP läuft bei einem anderen Verein oder Arbeitgeber? Wechsel des Berechtigungsgebers zum FZT</p>
          <ul className="mode-card__features">
            <li>Bisherigen Berechtigungsgeber angeben</li>
            <li>Passkopie hochladen — den Rest übernehmen wir</li>
            <li>Kein Neuantrag nötig, ZÜP bleibt aufrecht</li>
          </ul>
          <div className="mode-card__cta">
            <span className="mode-card__btn">Weiter</span>
            <svg className="mode-card__arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </div>
        </div>

        {meState === 'ok' && (
          <div
            className="mode-card"
            onClick={() => handleSelect('dokument-nachreichen')}
          >
            <div className="mode-card__accent mode-card__accent--renewal" />
            <div className="mode-card__icon mode-card__icon--renewal">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <path d="M17 8l-5-5-5 5M12 3v12" />
              </svg>
            </div>
            <h2 className="mode-card__title">Dokument nachreichen</h2>
            <p className="mode-card__desc">Neuen Ausweis oder Strafregisterauszug hinterlegen — jederzeit, ohne Antrag</p>
            <ul className="mode-card__features">
              <li>Immer möglich, kein Zeitfenster</li>
              <li>Reisepass, Personalausweis, Strafregisterauszug</li>
              <li>Wir prüfen und übernehmen es in deine Akte</li>
            </ul>
            <div className="mode-card__cta">
              <span className="mode-card__btn">Weiter</span>
              <svg className="mode-card__arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
            </div>
          </div>
        )}

        {meState === 'ok' && (itBezug || effectiveAdmin) && (
          <div
            className="mode-card"
            onClick={() => handleSelect('strafregister-it')}
          >
            <div className="mode-card__accent mode-card__accent--badge" />
            <div className="mode-card__icon mode-card__icon--badge">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <path d="M14 2v6h6M8 13h8M8 17h5" />
              </svg>
            </div>
            <h2 className="mode-card__title">Strafregisterauszug (Italien)</h2>
            <p className="mode-card__desc">Antrag an das Strafregisteramt Bozen — Formblatt füllen wir für dich aus</p>
            <ul className="mode-card__features">
              <li>Amtliches Formblatt automatisch befüllt</li>
              <li>Unterschreiben direkt am Handy oder mit der Maus</li>
              <li>PDF herunterladen und selbst per E-Mail schicken — Mailtext liegt bei</li>
            </ul>
            <div className="mode-card__cta">
              <span className="mode-card__btn">Weiter</span>
              <svg className="mode-card__arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
            </div>
          </div>
        )}

        <div
          className="mode-card"
          onClick={() => handleSelect('mein-zuep')}
        >
          <div className="mode-card__accent mode-card__accent--badge" />
          <div className="mode-card__icon mode-card__icon--badge">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="8" r="4" />
              <path d="M4 21c0-4 3.5-7 8-7s8 3 8 7" />
            </svg>
          </div>
          <h2 className="mode-card__title">Mein ZÜP-Datensatz</h2>
          <p className="mode-card__desc">Nur-lesende Übersicht deiner eigenen ZÜP-Daten und deren Status</p>
          <ul className="mode-card__features">
            <li>Persönliche Daten, Wohnsitze, Beschäftigungen</li>
            <li>ZÜP-Status &amp; Fristen</li>
            <li>Offene Punkte als To-do-Liste</li>
          </ul>
          <div className="mode-card__cta">
            <span className="mode-card__btn">Ansehen</span>
            <svg className="mode-card__arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </div>
        </div>

        {effectiveAdmin && (
          <div
            className="mode-card"
            onClick={() => handleSelect('antraege-admin')}
          >
            <div className="mode-card__accent mode-card__accent--badge" />
            <div className="mode-card__icon mode-card__icon--badge">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <path d="M14 2v6h6M9 15l2 2 4-4" />
              </svg>
            </div>
            <h2 className="mode-card__title">Anträge (Admin)</h2>
            <p className="mode-card__desc">Über das Portal eingereichte Anträge prüfen, übernehmen und freigeben</p>
            <ul className="mode-card__features">
              <li>Queue mit Status-Filter</li>
              <li>ZIP-Download zur Prüfung</li>
              <li>Freigabe mit Server-Guards (Doppelkosten-Schutz)</li>
            </ul>
            <div className="mode-card__cta">
              <span className="mode-card__btn">Öffnen</span>
              <svg className="mode-card__arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
            </div>
          </div>
        )}

        {effectiveAdmin && (
          <div
            className="mode-card"
            onClick={() => handleSelect('matching-review')}
          >
            <div className="mode-card__accent mode-card__accent--badge" />
            <div className="mode-card__icon mode-card__icon--badge">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 11l3 3L22 4" />
                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
              </svg>
            </div>
            <h2 className="mode-card__title">Matching-Review (Admin)</h2>
            <p className="mode-card__desc">Mitglieder mit unsicheren ZÜP-Zuordnungen manuell verknüpfen</p>
            <ul className="mode-card__features">
              <li>Automatisch/Kandidaten/Kein-Treffer-Übersicht</li>
              <li>Kandidaten mit Score prüfen</li>
              <li>Verknüpfen oder als „kein Treffer" markieren</li>
            </ul>
            <div className="mode-card__cta">
              <span className="mode-card__btn">Öffnen</span>
              <svg className="mode-card__arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
            </div>
          </div>
        )}
      </div>

      <p className="mode-footer-hint">
        Bei Unsicherheit: Die <strong>jährliche ZÜP-Verlängerung</strong> ist der häufigste Fall.
        Der Flughafenausweis muss nur alle 5 Jahre erneuert werden.
      </p>
    </div>
  );
}

export default ModeSelection;
